import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, Row, message, Typography, Skeleton, Table, notification } from "antd";

import { getDependencyDeleted, getDependenciesDeletedByCode } from "../../../../API/dependencies";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import ExportButton from '../../../Common/ExportButton/ExportButton';
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import DependencySearch from "../DependencySearch/DependencySearch";
import Columns from "./columns";

import { useAuth } from "../../../Context/AuthContext";

const DependencyDeleted = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Notificaciones
  const [api, contextHolder] = notification.useNotification();
  // Data
  const [dependencyDeleted, setDependencyDeleted] = useState(null);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  // Obtener filtros desde la URL (si vienen desde el dashboard o la vista original)
  const [searchParams] = useSearchParams();
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];

  useEffect(() => {
    const fetchDependencyDeleted = async () => {
      try {
        const data = await getDependencyDeleted();
        setDependencyDeleted(data);
        // Apply URL filters if provided
        let filtered = data;
        if (assetsFromUrl.length) {
          filtered = filtered.filter(item => assetsFromUrl.includes(item.asset?.name));
        }
        if (areasFromUrl.length) {
          filtered = filtered.filter(item => areasFromUrl.includes(item.asset?.area?.name));
        }
        setFilteredData(filtered);
      } catch (error) {
        message.error("No se encontraron las dependencias eliminados.");
      }
    };

    fetchDependencyDeleted();
  }, []);

  const filterParams = new URLSearchParams();
  if (assetsFromUrl.length) filterParams.set('assets', assetsFromUrl.join(','));
  if (areasFromUrl.length) filterParams.set('areas', areasFromUrl.join(','));

  const breadCrumbUrl = [
    { label: "Dependencias", url: "/dependencies", search: filterParams.toString() ? `?${filterParams.toString()}` : '' },
    { label: "Eliminadas" },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getDependenciesDeletedByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const dependenciesDeletedTableFilter = () => {
    const dependencyTypes = Array.from(
      new Map(dependencyDeleted.map(d => [d.type?.id, d.type]).filter(([id, type]) => id && type)).values()
    );
  
    const dependencyAssets = Array.from(
      new Map(dependencyDeleted.map(d => [d.asset?.code, d.asset]).filter(([code, a]) => code && a)).values()
    );

    const dependencyAreas = Array.from(
      new Map(dependencyDeleted.map(d => [d.asset?.area?.id, d.asset?.area]).filter(([id, area]) => id && area)).values()
    );

    return {
      filterTypes: dependencyTypes,
      filterAssets: dependencyAssets,
      filterAreas: dependencyAreas,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      //hasAnyRole={hasAnyRole}
      requiredPermission="read"
      //currentPermission={canAssetsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="space-between">
            <Col span={18}>
              <PageTitle title="Dependencias Eliminadas" icon="dependencies" />
            </Col>
          </Row>

          <Row justify="start" align="top">
            <Col span={18} style={{ display: "flex", alignItems: "center" }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} />
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <DependencySearch
                dependencies={dependencyDeleted}
                setFilteredData={setFilteredData}
              />
            </Col>
          </Row>

          <Row justify="end">
            <Col span={6} offset={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Dependencias Eliminadas.csv"
                transformData={(data) =>
                  data.map(item => ({
                    Activos: item.asset?.name || 'N/A',
                    Nombre: item.name,
                    Descripción: item.description,
                    Atributos: Array.isArray(item.atributes) && item.atributes.length > 0
                      ? item.atributes
                          .map(attr =>
                            attr?.atribute?.key
                              ? `${attr.atribute.key}: ${attr.value ?? ''}`
                              : null
                          )
                          .filter(Boolean)
                          .join(', ') || 'N/A'
                      : 'N/A',
                    Tipo: item.type?.name || 'N/A',
                    Responsable: item.asset?.area?.name || 'N/A'
                  }))
                }
              />
            </Col>
          </Row>
        </div>
      {
        !dependencyDeleted ? (
        <>
          <Skeleton active paragraph={{ rows: 6 }} />
          <div style={{ height: 32 }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
          </Row>
        </>
        ) : (
          <>
            <Table
              className="custom-pagination deleted-table "
              columns={Columns(handleOpenChangeLog, dependenciesDeletedTableFilter)}
              dataSource={filteredData} 
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />
            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </>
        )
      }
      </>
    </WithPermission>
  );
};

export default DependencyDeleted;